import React, { createContext, useContext, useEffect, useState } from 'react';
import { locationsApi, type LocationItem } from '../api/locationsApi';

const LAST_LOCATION_KEY = 'lastLocationId';

interface LocationContextValue {
    locations: LocationItem[];
    loading: boolean;
    error: string | null;
    locationId: string;
    setLocationId: (ghlId: string) => void;
    currentLocation: LocationItem | null;
    refreshLocations: () => Promise<void>;
    setLocalSyncActive: (ghlId: string, emr: string, syncActive: boolean) => void;
}

const LocationContext = createContext<LocationContextValue | null>(null);

export const LocationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [locations, setLocations] = useState<LocationItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [locationId, setLocationIdState] = useState(() => sessionStorage.getItem(LAST_LOCATION_KEY) || '');

    const applyLocationList = (list: LocationItem[]) => {
        setLocations(list);
        setLocationIdState(current => {
            if (current && list.some(l => l.ghl_id === current)) return current;
            return list[0]?.ghl_id ?? '';
        });
    };

    useEffect(() => {
        locationsApi.list()
            .then(applyLocationList)
            .catch(() => setError('Could not load locations'))
            .finally(() => setLoading(false));
    }, []);

    const setLocationId = (ghlId: string) => {
        setLocationIdState(ghlId);
        sessionStorage.setItem(LAST_LOCATION_KEY, ghlId);
    };

    const refreshLocations = async () => {
        const list = await locationsApi.list();
        applyLocationList(list);
    };

    const setLocalSyncActive = (ghlId: string, emr: string, syncActive: boolean) => {
        setLocations(prev =>
            prev.map(loc =>
                loc.ghl_id === ghlId && loc.emr === emr
                    ? { ...loc, sync_active: syncActive }
                    : loc
            )
        );
    };

    const currentLocation = locations.find(l => l.ghl_id === locationId) ?? null;

    return (
        <LocationContext.Provider
            value={{
                locations,
                loading,
                error,
                locationId,
                setLocationId,
                currentLocation,
                refreshLocations,
                setLocalSyncActive,
            }}
        >
            {children}
        </LocationContext.Provider>
    );
};

export function useLocationContext(): LocationContextValue {
    const ctx = useContext(LocationContext);
    if (!ctx) throw new Error('useLocationContext must be used within a LocationProvider');
    return ctx;
}
